import { Flex, FormControl, Input, Stack } from "@chakra-ui/react"
import dayjs from "dayjs"
import { ErrorMessage, Formik } from "formik"
import { ComponentProps, Dispatch, FC, SetStateAction, useEffect } from "react"
import { FormDynamicField } from "src/components/shared/FormDynamicField"
import { ReactSelect, SelectOption } from "src/components/shared/ReactSelect"
import { ErrorMessageField } from "src/components/ui"
import { DrawerForm } from "src/components/ui/DrawerForm"
import { InputLabel } from "src/components/ui/InputLabel"
import { IIncomeCategory } from "src/domain/entities"
import { useContactListService, useShopListService } from "src/hooks"
import { getFullName } from "src/utils/helpers"
import { FormikOnSubmit } from "src/utils/types"
import { IHallBookingReceiptAddFormFields } from "./IHallBookingReceiptAddFormFields"

interface Props extends Omit<ComponentProps<typeof DrawerForm>, "children"> {
	handleSubmit: FormikOnSubmit<IHallBookingReceiptAddFormFields>
	hallBookingReceiptCategoryList: IIncomeCategory[]
	hallBookingReceiptNextNumber: number
	getGujaratiSuggestions: (value: string) => Promise<string[]>
	selectedHallBookingReceiptCategory?: IIncomeCategory
	setSelectedHallBookingReceiptCategory: Dispatch<
		SetStateAction<IIncomeCategory | undefined>
	>
}

export const HallBookingReceiptAddDrawerFormView: FC<Props> = ({
	hallBookingReceiptNextNumber,
	handleSubmit,
	hallBookingReceiptCategoryList,
	getGujaratiSuggestions,
	selectedHallBookingReceiptCategory,
	setSelectedHallBookingReceiptCategory,
	...rest
}) => {
	const { contactList, fetchContactList } = useContactListService()
	const { shopList, fetchShopList } = useShopListService()

	useEffect(() => {
		fetchContactList()
		fetchShopList({ fetch: { currentRentee: true } })
	}, [fetchContactList, fetchShopList])

	return (
		<Formik<IHallBookingReceiptAddFormFields>
			initialValues={{
				categoryId: selectedHallBookingReceiptCategory?.id ?? "",
				hallBookingReceiptNo: hallBookingReceiptNextNumber,
				date: dayjs().format("YYYY-MM-DD") as any,
				fields:
					selectedHallBookingReceiptCategory?.fields?.map((field) => ({
						fieldId: field.id,
						value: "",
					})) ?? [],
			}}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			{({ values, isSubmitting, handleChange, setFieldValue }) => {
				return (
					<DrawerForm
						size="lg"
						headerLabel="Add Hall Booking Receipt"
						submitLabel="Save"
						isSubmitting={isSubmitting}
						{...rest}
					>
						<Stack maxWidth={"2xl"} marginX={"auto"} gridGap={2}>
							{/* HallBookingCategory */}
							<FormControl>
								<InputLabel label="Hall Booking Receipt Category" />
								<ReactSelect
									name="categoryId"
									onChange={(newValue) => {
										setFieldValue(
											"categoryId",
											(newValue as SelectOption).value,
										)
										setSelectedHallBookingReceiptCategory(
											hallBookingReceiptCategoryList.find(
												(category) =>
													category.id ===
													(newValue as SelectOption).value,
											),
										)
									}}
									options={hallBookingReceiptCategoryList.map(
										(incomeCategory) => ({
											label: incomeCategory.name,
											value: incomeCategory.id,
										}),
									)}
									isSearchable
								/>
							</FormControl>
							<Flex gridColumnGap={2}>
								{/* Date */}
								<FormControl flex={1}>
									<InputLabel label="Date" />
									<Input
										type="date"
										name="date"
										maxLength={100}
										defaultValue={values.date}
										onChange={handleChange}
									/>
									<ErrorMessage
										component={ErrorMessageField}
										name="receiptDate"
									/>
								</FormControl>
								{/* Receipt No. */}
								<FormControl flex={1}>
									<InputLabel label="Receipt No." />
									<Input
										name="receiptNo"
										maxLength={100}
										required
										autoFocus
										value={values.hallBookingReceiptNo}
										onChange={handleChange}
									/>
									<ErrorMessage
										component={ErrorMessageField}
										name="incomeNo"
									/>
								</FormControl>
							</Flex>

							<Flex gridRowGap={2} direction="column">
								{selectedHallBookingReceiptCategory?.fields
									?.sort((a, b) => a.position - b.position)
									.map((field, i) => {
										return (
											<FormDynamicField
												key={field.id}
												field={field}
												value={values.fields[i]}
												controlName={`fields[${i}]`}
												handleChange={handleChange}
												setFieldValue={(fieldName, payload) => {
													if (field.name === "Contact") {
														const findContact =
															contactList.find(
																(e) =>
																	e.id ===
																	payload.value,
															)
														const findAddressFieldIndex =
															selectedHallBookingReceiptCategory?.fields?.findIndex(
																(e) =>
																	e.name ===
																	"Contact address",
															)

														if (
															findAddressFieldIndex !==
																undefined &&
															findAddressFieldIndex !== -1
														) {
															const addressField =
																selectedHallBookingReceiptCategory
																	?.fields?.[
																	findAddressFieldIndex
																]

															setFieldValue(
																`fields[${findAddressFieldIndex}]`,
																{
																	fieldId:
																		addressField?.id,
																	value: findContact?.address,
																},
															)
														}
													} else if (field.name === "Shop") {
														const findShop = shopList.find(
															(e) => e.id === payload.value,
														)

														// Set automatic rent per month
														const findRentPerMonthFieldIndex =
															selectedHallBookingReceiptCategory?.fields?.findIndex(
																(e) =>
																	e.name ===
																	"Rent per month",
															)

														if (
															findRentPerMonthFieldIndex !==
																undefined &&
															findRentPerMonthFieldIndex !==
																-1
														) {
															const rentPerMonthField =
																selectedHallBookingReceiptCategory
																	?.fields?.[
																	findRentPerMonthFieldIndex
																]

															setFieldValue(
																`fields[${findRentPerMonthFieldIndex}]`,
																{
																	fieldId:
																		rentPerMonthField?.id,
																	value: findShop?.rent,
																},
															)
														}

														// Set automatic amount
														const findAmountFieldIndex =
															selectedHallBookingReceiptCategory?.fields?.findIndex(
																(e) =>
																	e.name === "Amount",
															)

														if (
															findAmountFieldIndex !==
																undefined &&
															findAmountFieldIndex !== -1
														) {
															const amountField =
																selectedHallBookingReceiptCategory
																	?.fields?.[
																	findAmountFieldIndex
																]

															setFieldValue(
																`fields[${findAmountFieldIndex}]`,
																{
																	fieldId:
																		amountField?.id,
																	value:
																		(findShop?.rent ??
																			0) * 12,
																},
															)
														}

														// Set automatic contact
														const findContactFieldIndex =
															selectedHallBookingReceiptCategory?.fields?.findIndex(
																(e) =>
																	e.name === "Contact",
															)

														if (
															findContactFieldIndex !==
																undefined &&
															findContactFieldIndex !== -1
														) {
															const contactField =
																selectedHallBookingReceiptCategory
																	?.fields?.[
																	findContactFieldIndex
																]

															setFieldValue(
																`fields[${findContactFieldIndex}]`,
																{
																	fieldId:
																		contactField?.id,
																	value: contactList
																		.map((c) => ({
																			label: getFullName(
																				c,
																			),
																			value: c,
																		}))
																		.find(
																			(c) =>
																				c.value
																					.id ===
																				findShop
																					?.currentRentee
																					?.id,
																		)?.value,
																},
															)
														}
													}

													setFieldValue(fieldName, payload)
												}}
											/>
										)
									})}
							</Flex>
						</Stack>
					</DrawerForm>
				)
			}}
		</Formik>
	)
}
